import { Middleware } from "redux";
import { toastMiddleware } from "@teamprevent/common/toasts/toastMiddleware";
import { auth } from "@teamprevent/common";
import { appInsightsTrackPageViewMiddleware } from "@teamprevent/common/logging/appInsightsTrackPageViewMiddleware";
import { appInsightsLogExceptionMiddleware } from "@teamprevent/common/logging/appInsightsLogExceptionMiddleware";
import { restrictRoutesByPhysioOriginsMiddleware } from "@teamprevent/common/productRestriction/restrictRoutesByPhysioOriginsMiddleware";
import { configureDispatchActions } from "@neworbit/redux-helpers";
import { configureStore as toolkitConfigureStore  } from "@reduxjs/toolkit";
import { useDispatch as useVanillaDispatch, useSelector as vanillaUseSelector } from "react-redux";

import { reducer as loading } from "redux-request-loading";

import { ApplicationState } from "./applicationState";
import { routes } from "./routes";
import { reducers } from "./reducers";

import { routerForBrowser, LOCATION_CHANGED, replace } from "redux-little-router";

const configureStore = () => {
    // Wire up middleware that allows us to call a function (e.g. to load data) when you navigate to a route,
    // by setting it as the "resolve" property on the route.
    const dispatchActions = configureDispatchActions(
        LOCATION_CHANGED,
        action => (action.payload || {}).result,
        state => state.router.params
    );

    const restrictPhysioOriginsMiddleware = restrictRoutesByPhysioOriginsMiddleware<ApplicationState>({
        getPhysioOrigins: (state) => state.allowedPhysioOrigins,
        notAllowedAction: replace("/")
    });
    const { reducer, middleware, enhancer } = routerForBrowser({
        routes,
    });

    return toolkitConfigureStore({
        reducer: { ...reducers, router: reducer, loading },
        middleware: getDefaultMiddleware => [
            auth.unauthenticatedRedirectMiddleware as Middleware,
            appInsightsTrackPageViewMiddleware,
            appInsightsLogExceptionMiddleware,
            toastMiddleware as Middleware,
            dispatchActions as Middleware,
            ...getDefaultMiddleware({ serializableCheck: false, immutableCheck: false }),
            middleware,
            restrictPhysioOriginsMiddleware
        ],
        enhancers: defaultEnhancers => [enhancer, ...defaultEnhancers],
    });
};

type enhancedDispatch = ReturnType<typeof configureStore>["dispatch"];
const useDispatch: () => enhancedDispatch | ((action: any) => void | Promise<any>) = useVanillaDispatch;
const useSelector = vanillaUseSelector;

export { useSelector, useDispatch, configureStore };
